import React, { useMemo, useCallback, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType
} from 'reactflow';
import { User, Calendar, MapPin } from 'lucide-react';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Heritage color palette
const COLORS = {
  heritageGreen: '#2C4A3B',
  warmSand: '#E6E2D3',
  goldAccent: '#C9A227',
  darkText: '#1a1a1a',
  lightText: '#f5f5f5',
  maleNode: '#2C4A3B',
  femaleNode: '#4A2C3B',
  unknownNode: '#3B3B2C'
};

// Custom node for Pedigree Chart
const PedigreeNode = ({ data }) => {
  const bgColor = data.gender === 'male' ? COLORS.maleNode : 
                  data.gender === 'female' ? COLORS.femaleNode : 
                  COLORS.unknownNode;
  
  return (
    <div 
      className="relative rounded-lg shadow-lg border-2 transition-all duration-200 hover:shadow-xl"
      style={{ 
        backgroundColor: bgColor,
        borderColor: data.isRoot ? COLORS.goldAccent : 'rgba(255,255,255,0.2)',
        borderWidth: data.isRoot ? '3px' : '1px',
        minWidth: '180px',
        maxWidth: '200px'
      }}
    >
      <Handle type="target" position={Position.Left} style={{ background: COLORS.goldAccent }} />
      <Handle type="source" position={Position.Right} style={{ background: COLORS.goldAccent }} />
      
      <div className="p-3">
        {/* Photo or Avatar */}
        <div className="flex items-center gap-3 mb-2">
          {data.photoUrl ? (
            <img 
              src={data.photoUrl} 
              alt={data.name}
              className="w-10 h-10 rounded-full object-cover border-2"
              style={{ borderColor: COLORS.warmSand }}
            />
          ) : (
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
            >
              <User className="w-5 h-5" style={{ color: COLORS.warmSand }} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: COLORS.warmSand }}>
              {data.name || 'Unknown'}
            </p>
            {data.generation !== undefined && (
              <p className="text-[10px] opacity-60" style={{ color: COLORS.warmSand }}>
                Generation {data.generation}
              </p>
            )}
          </div>
        </div>
        
        {/* Details */}
        <div className="space-y-1 text-xs" style={{ color: COLORS.warmSand }}>
          {data.birthYear && (
            <div className="flex items-center gap-1.5 opacity-80">
              <Calendar className="w-3 h-3" />
              <span>b. {data.birthYear}</span>
              {data.deathYear && <span>- d. {data.deathYear}</span>}
            </div>
          )}
          {data.birthPlace && (
            <div className="flex items-center gap-1.5 opacity-70 truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{data.birthPlace}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const nodeTypes = { pedigree: PedigreeNode };

// Build pedigree tree structure
const buildPedigreeLayout = (individuals, relationships, rootId, maxGenerations = 5) => {
  const nodes = [];
  const edges = [];
  
  const indById = {};
  individuals.forEach(ind => { indById[ind.id] = ind; });
  
  // Build parent lookup
  const childToParents = {};
  relationships.forEach(rel => {
    if (rel.relationship_category === 'parent_child' && rel.child_id && rel.parent_id) {
      if (!childToParents[rel.child_id]) {
        childToParents[rel.child_id] = [];
      }
      childToParents[rel.child_id].push(rel.parent_id);
    }
  });
  
  // Helper to extract year
  const extractYear = (dateStr) => {
    if (!dateStr) return null;
    const match = dateStr.match(/(\d{4})/);
    return match ? parseInt(match[1]) : null;
  };
  
  // BFS to build ancestor tree
  const visited = new Set();
  const queue = [{ id: rootId, generation: 0, y: 0 }];
  const levelCounts = {};
  
  while (queue.length > 0) {
    const { id, generation, y } = queue.shift();
    
    if (visited.has(id) || generation > maxGenerations) continue;
    visited.add(id);
    
    const ind = indById[id];
    if (!ind) continue;
    
    // Track position in level
    if (!levelCounts[generation]) levelCounts[generation] = 0;
    const posInLevel = levelCounts[generation]++;
    
    // Calculate position - ancestors spread out to the right
    const x = generation * 280;
    const yOffset = posInLevel * 150 - (levelCounts[generation] - 1) * 75;
    
    const name = `${ind.given_names || ''} ${ind.surname || ''}`.trim();
    
    nodes.push({
      id: ind.id,
      type: 'pedigree',
      position: { x, y: yOffset },
      data: {
        name,
        gender: ind.sex ?? ind.gender,
        birthYear: extractYear(ind.birth_date),
        deathYear: extractYear(ind.death_date),
        birthPlace: ind.birth_place,
        photoUrl: resolveAssetUrl(BACKEND_URL, ind.profile_image_url ?? ind.photo_url),
        generation,
        isRoot: generation === 0
      }
    });
    
    // Add parents to queue
    const parents = childToParents[id] || [];
    parents.forEach((parentId, idx) => {
      if (!visited.has(parentId)) {
        queue.push({ 
          id: parentId, 
          generation: generation + 1,
          y: yOffset + (idx === 0 ? -75 : 75)
        });
        
        edges.push({
          id: `${parentId}-${id}`,
          source: parentId,
          target: id,
          type: 'smoothstep',
          style: { stroke: COLORS.goldAccent, strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: COLORS.goldAccent }
        });
      }
    });
  }
  
  return { nodes, edges };
};

const PedigreeChart = ({ 
  individuals = [], 
  relationships = [], 
  rootPersonId,
  onPersonClick
}) => {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    if (!rootPersonId || individuals.length === 0) {
      return { nodes: [], edges: [] };
    }
    return buildPedigreeLayout(individuals, relationships, rootPersonId);
  }, [individuals, relationships, rootPersonId]);
  
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Sync when rootPersonId, individuals, or relationships change
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onNodeClick = useCallback((event, node) => {
    if (onPersonClick) {
      onPersonClick(node.id);
    }
  }, [onPersonClick]);
  
  if (!rootPersonId) {
    return (
      <div className="flex items-center justify-center h-full" style={{ backgroundColor: COLORS.warmSand }}>
        <p className="text-lg" style={{ color: COLORS.heritageGreen }}>
          Select a person to view their pedigree chart
        </p>
      </div>
    );
  }
  
  return (
    <div className="w-full h-full" style={{ backgroundColor: COLORS.warmSand }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      >
        <Background color={COLORS.heritageGreen} gap={20} size={1} style={{ opacity: 0.05 }} />
        <Controls 
          style={{ 
            backgroundColor: COLORS.heritageGreen,
            borderRadius: '8px',
            border: 'none'
          }}
        />
        <MiniMap 
          nodeColor={(node) => node.data?.gender === 'male' ? COLORS.maleNode : COLORS.femaleNode}
          style={{ backgroundColor: 'rgba(44, 74, 59, 0.1)' }}
        />
      </ReactFlow>
    </div>
  );
};

export default PedigreeChart;
